import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { withNamespaces } from "react-i18next";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import enGB from "date-fns/locale/en-GB";
import { fetchCalendarByContract } from "../actions/calendars";
import { format } from "date-fns";
import { fromZonedTime } from "date-fns-tz";
import { Grid2x2Check, Info, ChevronLeft } from "lucide-react";

import AppRentalHeader from "../containers/AppRentalHeader";
import { fetchDisponibility, fetchAvailability } from "../actions/qrsrental";
import NmStepBarRental from "../components/NmStepBarRental";
import { QuantityRentalStepper } from "../components/QuantityPickers";
import { currencySymbol } from '../utils/currency';

registerLocale("en-GB", enGB);

const TZ = "Europe/Madrid";

function madrid1500ToUtc(dateObj) {
  const ymd = format(dateObj, "yyyy-MM-dd");
  return fromZonedTime(`${ymd} 15:00:00`, TZ);
}

function applyBusinessRules(totalAmount, daysSelected, businessRules) {
  const activeRules = businessRules.filter(
    (rule) => rule.Active && rule.Type === "MaxDayDiscount"
  );
  if (!activeRules.length) return { total: totalAmount, applied: null };
  activeRules.sort((a, b) => b.MaxDays - a.MaxDays);
  const matchingRule = activeRules.find((rule) => daysSelected >= rule.MaxDays);
  if (matchingRule) {
    const discount = matchingRule.DiscountPercentage;
    return { total: totalAmount * (1 - discount / 100), applied: matchingRule };
  }
  return { total: totalAmount, applied: null };
}

const ORDERED_LOCKER_KEYS = [
  "BNS","CNS","BWS","CWS","BNM","CNM","BWM","CWM",
  "BNL","CNL","BWL","CWL","BNXL","CNXL","BWXL","CWXL",
  "BN2XL","CN2XL","BW2XL","CW2XL","BN3XL","CN3XL","BW3XL","CW3XL",
];

class LockerSelectorRentalView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      step: 1,
      lockers: null,
      prices: null,
      selectedLockerType: null,
      selectedQuantity: 1,
      startDate: null,
      endDate: null,
      selectedInfo: null,
      qrType: "pay",
      errorMessage: false,
      isDurationExceeded: false,
      priceCalendar: {},
      calendarMonth: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      limitDays: 13,
      selectedPrices: [],
      totalAmount: 0,
      useUnifiedUI: true,
      isTypeModalOpen: false,
      isQtyModalOpen: false,
      isDatesModalOpen: false,
      isLoading: false,
      activePanel: "type",
      businessRules: [],
      appliedRule: null,
      panelLeft: 0,
      contract: null,
      terms: '',
      workingMode: null,
      hybridMode: false,
      measureModal: false,
      measureType: null,
    };

    this.formRef = React.createRef();
    this.typeBtnRef = React.createRef();
    this.qtyBtnRef = React.createRef();
    this.datesStartBtnRef = React.createRef();

    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.handleStartDateChange = this.handleStartDateChange.bind(this);
    this.handleEndDateChange = this.handleEndDateChange.bind(this);
    this.handleContinue = this.handleContinue.bind(this);
  }

  openModal = (key) => this.setState({ [key]: true });
  closeModal = (key) => this.setState({ [key]: false });

  openPanel = (panel) => {
    let btnRef = null;
    if (panel === "type") btnRef = this.typeBtnRef;
    if (panel === "qty") btnRef = this.qtyBtnRef;
    if (panel === "dates") btnRef = this.datesStartBtnRef;

    if (btnRef?.current && this.formRef?.current) {
      const btnRect = btnRef.current.getBoundingClientRect();
      const formRect = this.formRef.current.getBoundingClientRect();
      this.setState({ activePanel: panel, panelLeft: btnRect.left - formRect.left });
    } else {
      this.setState({ activePanel: panel, panelLeft: 0 });
    }
  };
  closePanel = () => this.setState({ activePanel: null });

  componentDidMount() {
    const { zoneSelected, qr } = this.props.location.state;
    const qrtoken = qr.toString() + zoneSelected.toString();

    this.props.fetchAvailability(qrtoken)
      .then((answer) => {
        const answerLockers = answer.response.answerLockers;
        this.setState(
          {
            lockers: answerLockers.Disponibility,
            prices: answerLockers.Prices,
            qrType: answerLockers.qrType,
            contract: answerLockers.Contract,
            terms: answerLockers.Terms,
            workingMode: answerLockers.WorkingMode,
            hybridMode: answerLockers.HybridMode || false,
            isTypeModalOpen: true,
          },
          () => {
            setTimeout(() => {
              if (this.typeBtnRef?.current) this.openPanel("type");
            }, 0);
          }
        );
      })
      .catch(console.error);
  }

  selectLockerType(lockerType) {
    const lockerPrice = this.getLockerPrice(lockerType);
    const { zoneSelected, descriptionZone } = this.props.location.state;
    const { contract } = this.state;

    this.setState({
      selectedLockerType: lockerType,
      selectedInfo: { lockerType, lockerPrice, zoneSelected, descriptionZone },
      step: 2,
      priceCalendar: {},
      ...(this.state.useUnifiedUI ? { isTypeModalOpen: false } : {}),
    });

    this.props.fetchCalendarByContract(contract, lockerType, zoneSelected)
      .then((response) => {
        const calendarArray = response?.response?.PriceCalendar || [];
        const rulesFromOperator = (response?.response?.BusinessRules || []).filter(r => r && r.Active);

        const flatPriceMap = {};
        calendarArray.forEach((entry) => {
          const d = new Date(entry.date);
          const isoDate = d.toLocaleDateString("en-CA", { timeZone: "Europe/Madrid" });
          const matchedPrice = entry.prices.find(p => p.zone === zoneSelected && p.type === lockerType);
          if (matchedPrice) flatPriceMap[isoDate] = matchedPrice.price;
        });

        this.setState({ priceCalendar: flatPriceMap, businessRules: rulesFromOperator });
      })
      .catch(console.error);
  }

  renderMonthGrid(monthDate) {
    const { priceCalendar, startDate, endDate, calendarMonth, selectedQuantity } = this.state;
    const { t } = this.props;

    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const blanks = Array(firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1).fill(null);

    const weekDays = [t("Lun"), t("Mar"), t("Mie"), t("Jue"), t("Vie"), t("Sab"), t("Dom")];
    const title = firstDay.toLocaleString("default", { month: "long", year: "numeric" });

    const days = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const iso = date.toLocaleDateString("en-CA", { timeZone: "Europe/Madrid" });
      const price = priceCalendar[iso];

      const isStart = startDate && date.toDateString() === startDate.toDateString();
      const isEnd = endDate && date.toDateString() === endDate.toDateString();
      const inRange = startDate && endDate && date > startDate && date < endDate;
      const isPast = this.isBeforeToday(date);
      const isTodayAndBlocked = this.isToday(date) && this.isTodayBlocked();
      const isSingleDay = isStart && isEnd && startDate?.toDateString() === endDate?.toDateString();

      let cls = "nm-cal-cell";
      if (isSingleDay) cls += " selected single";
      else {
        if (isStart) cls += " selected start";
        if (isEnd) cls += " selected end";
        if (inRange) cls += " in-range";
      }
      if (isPast || !price || isTodayAndBlocked) cls += " disable";

      days.push(
        <div
          key={iso}
          className={cls}
          onClick={() => { if (!isPast && price && !isTodayAndBlocked) this.handleCalendarDateClick(new Date(date)); }}
          title={price ? `${t("Precio")}: ${(price * selectedQuantity).toFixed(2)} ${currencySymbol()}` : t("Sin precio")}
        >
          <div className="day">{d}</div>
          <div className="price">{price && !isPast ? `${parseFloat((price * selectedQuantity).toFixed(2))} ${currencySymbol()}` : "-"}</div>
        </div>
      );
    }

    return (
      <div className="nm-cal-month-grid">
        <div className="nm-cal-header">
          <button
            className="nm-cal-nav"
            disabled={this.isCurrentMonthOrBefore(calendarMonth)}
            onClick={() => {
              if (!this.isCurrentMonthOrBefore(calendarMonth)) {
                this.setState({ calendarMonth: new Date(calendarMonth.setMonth(calendarMonth.getMonth() - 1)) });
              }
            }}
          >
            &lt;
          </button>
          <h6 className="nm-cal-month">{title.charAt(0).toUpperCase() + title.slice(1)}</h6>
          <button
            className="nm-cal-nav"
            onClick={() => this.setState({ calendarMonth: new Date(calendarMonth.setMonth(calendarMonth.getMonth() + 1)) })}
          >
            &gt;
          </button>
        </div>
        <div className="nm-cal-grid">
          {weekDays.map(day => <div key={day} className="nm-cal-cell weekday-header">{day}</div>)}
          {blanks.map((_, i) => <div key={`blank-${i}`} className="nm-cal-cell empty" />)}
          {days}
        </div>
      </div>
    );
  }

  renderMonthGridPC(monthDate, isFirstMonth) {
    const { priceCalendar, startDate, endDate, calendarMonth, selectedQuantity } = this.state;
    const { t } = this.props;

    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const blanks = Array(firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1).fill(null);

    const weekDays = [t("Lun"), t("Mar"), t("Mie"), t("Jue"), t("Vie"), t("Sab"), t("Dom")];
    const title = firstDay.toLocaleString("default", { month: "long", year: "numeric" });

    const days = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const iso = date.toLocaleDateString("en-CA", { timeZone: "Europe/Madrid" });
      const price = priceCalendar[iso];

      const isStart = startDate && date.toDateString() === startDate.toDateString();
      const isEnd = endDate && date.toDateString() === endDate.toDateString();
      const inRange = startDate && endDate && date > startDate && date < endDate;
      const isPast = this.isBeforeToday(date);
      const isTodayAndBlocked = this.isToday(date) && this.isTodayBlocked();
      const isSingleDay = isStart && isEnd && startDate?.toDateString() === endDate?.toDateString();

      let cls = "nm-cal-cell";
      if (isSingleDay) cls += " selected single";
      else {
        if (isStart) cls += " selected start";
        if (isEnd) cls += " selected end";
        if (inRange) cls += " in-range";
      }
      if (isPast || !price || isTodayAndBlocked) cls += " disable";

      days.push(
        <div
          key={iso}
          className={cls}
          onClick={() => { if (!isPast && price && !isTodayAndBlocked) this.handleCalendarDateClick(new Date(date)); }}
          title={price ? `${t("Precio")}: ${price * selectedQuantity} ${currencySymbol()}` : t("Sin precio")}
        >
          <div className="day">{d}</div>
          <div className="price">{price ? `${parseFloat((price * selectedQuantity).toFixed(2))} ${currencySymbol()}` : "-"}</div>
        </div>
      );
    }

    return (
      <div className="nm-cal-month-grid">
        <div className="nm-cal-header">
          {isFirstMonth ? (
            <>
              <button
                className="nm-cal-nav"
                disabled={this.isCurrentMonthOrBefore(calendarMonth)}
                onClick={() => {
                  if (!this.isCurrentMonthOrBefore(calendarMonth)) {
                    this.setState({ calendarMonth: new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1) });
                  }
                }}
              >
                &lt;
              </button>
              <h6 className="nm-cal-month">{title.charAt(0).toUpperCase() + title.slice(1)}</h6>
            </>
          ) : (
            <>
              <h6 className="nm-cal-month">{title.charAt(0).toUpperCase() + title.slice(1)}</h6>
              <button
                className="nm-cal-nav"
                onClick={() => this.setState({ calendarMonth: new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1) })}
              >
                &gt;
              </button>
            </>
          )}
        </div>
        <div className="nm-cal-grid">
          {weekDays.map(day => <div key={day} className="nm-cal-cell weekday-header">{day}</div>)}
          {blanks.map((_, i) => <div key={`blank-${i}`} className="nm-cal-cell empty" />)}
          {days}
        </div>
      </div>
    );
  }

  renderPriceCalendarPC() {
    const { calendarMonth } = this.state;
    const nextMonth = new Date(calendarMonth);
    nextMonth.setMonth(calendarMonth.getMonth() + 1);
    return (
      <div className="nm-cal-dual">
        {this.renderMonthGridPC(calendarMonth, true)}
        {this.renderMonthGridPC(nextMonth, false)}
      </div>
    );
  }

  renderPriceCalendar() {
    const { calendarMonth } = this.state;
    return (
      <div className="nm-cal-dual">
        {this.renderMonthGrid(calendarMonth)}
      </div>
    );
  }

  handleCalendarDateClick = (clickedDate) => {
    const { startDate, endDate, priceCalendar, selectedQuantity } = this.state;

    if (startDate && clickedDate.toDateString() === startDate.toDateString()) {
      this.setState({ startDate: null, endDate: null, selectedPrices: [], totalAmount: 0, isDurationExceeded: false });
      return;
    }

    if (!startDate || clickedDate < startDate) {
      const iso = clickedDate.toLocaleDateString("en-CA", { timeZone: "Europe/Madrid" });
      const base = priceCalendar[iso] || 0;
      const dayPrice = base * (selectedQuantity || 1);
      this.setState({
        startDate: clickedDate,
        endDate: clickedDate,
        isDurationExceeded: false,
        selectedPrices: base ? [{ date: new Date(clickedDate), price: dayPrice }] : [],
        totalAmount: base ? dayPrice : 0,
      });
      return;
    }

    const diff = Math.ceil((clickedDate - startDate) / (1000 * 60 * 60 * 24));
    const tooLong = diff > this.state.limitDays;

    if (!tooLong) {
      const selectedPrices = [];
      let totalAmount = 0;
      for (let d = new Date(startDate); d <= clickedDate; d.setDate(d.getDate() + 1)) {
        const iso = d.toLocaleDateString("en-CA", { timeZone: "Europe/Madrid" });
        const price = priceCalendar[iso];
        if (price) {
          const dayPrice = price * selectedQuantity;
          selectedPrices.push({ date: new Date(d), price: dayPrice });
          totalAmount += dayPrice;
        }
      }
      const daysSelected = selectedPrices.length;
      const { total, applied } = applyBusinessRules(totalAmount, daysSelected, this.state.businessRules);
      this.setState({ endDate: clickedDate, isDurationExceeded: tooLong, selectedPrices, totalAmount: total, appliedRule: applied });
    } else {
      this.setState({ endDate: clickedDate, isDurationExceeded: true });
    }
  };

  handleQuantityChange(event) {
    this.setState({
      selectedQuantity: event.target.value,
      startDate: null, endDate: null, selectedPrices: [], totalAmount: 0, isDurationExceeded: false,
    });
  }

  handleStartDateChange(date) {
    const { endDate } = this.state;
    const localDate = new Date(date.setHours(15, 0, 0, 0));
    if (endDate) {
      const diff = Math.ceil((endDate - localDate) / (1000 * 60 * 60 * 24));
      this.setState({ startDate: localDate, isDurationExceeded: diff >= this.state.limitDays });
    } else {
      this.setState({ startDate: localDate });
    }
  }

  handleEndDateChange(date) {
    const { startDate } = this.state;
    const localDate = new Date(date.setHours(15, 0, 0, 0));
    if (startDate) {
      const diff = Math.ceil((localDate - startDate) / (1000 * 60 * 60 * 24));
      this.setState({ endDate: localDate, isDurationExceeded: diff >= this.state.limitDays });
    } else {
      this.setState({ endDate: localDate });
    }
  }

  handleContinue() {
    if (this.state.isLoading) return;
    this.setState({ isLoading: true, errorMessage: false });
    const { selectedLockerType, selectedQuantity, startDate, endDate, qrType, contract, terms } = this.state;
    const { zoneSelected, descriptionZone, qr } = this.props.location.state;

    const localStartDate = madrid1500ToUtc(this.state.startDate);
    const localEndDate = madrid1500ToUtc(this.state.endDate);
    const lockerQty = selectedQuantity == null ? 1 : Number(selectedQuantity);

    const bookingData = {
      Zone: zoneSelected,
      LockerType: selectedLockerType,
      LockerQty: lockerQty,
      RentalStartDate: localStartDate,
      RentalFinishDate: localEndDate,
      PaymentMethod: "TPV",
      Contract: contract,
    };

    this.props.fetchDisponibility(bookingData)
      .then((answer) => {
        if (answer.response.LockersDisponibility === true) {
          this.props.push("/PhoneRentalView", {
            zoneSelected,
            descriptionZone,
            selectedLocker: selectedLockerType,
            lockerPrice: answer.response.LockersAmount,
            lockerAssigned: answer.response.LockersAssigned,
            prerentCode: answer.response.PrerentCode,
            prerentTime: answer.response.PrerentTime,
            totalDays: answer.response.TotalDays,
            startDate,
            endDate,
            qrType,
            qr,
            timeStamp: answer.response.TimeStamp,
            selectedQuantity,
            customerId: answer.response.CustomerId,
            terms,
          });
        } else {
          this.setState({ errorMessage: true, isLoading: false });
        }
      })
      .catch((error) => {
        console.error(error);
        this.setState({ isLoading: false });
      });
  }

  getLockerType(type) {
    const map = {
      BNS: "Standard", CNS: "Standard Carga", BWS: "Standard XL", CWS: "Standard Carga XL",
      BNM: "Medium", CNM: "Medium Carga", BWM: "Medium XL", CWM: "Medium Carga XL",
      BNL: "Large", CNL: "Large Carga", BWL: "Large XL", CWL: "Large Carga XL",
      BNXL: "Large", CNXL: "Large Carga", BWXL: "Large XL", CWXL: "Large Carga XL",
      BN2XL: "Large", CN2XL: "Large Carga", BW2XL: "Large XL", CW2XL: "Large Carga XL",
      BN3XL: "Ski", CN3XL: "Ski Carga", BW3XL: "Ski XL", CW3XL: "Ski Carga XL",
    };
    return map[type] || "None";
  }

  getLockerPicture(type) { return `/images/measures/locker_final_${type}.png`; }

  getLockerPrice(type) {
    const { prices } = this.state;
    const obj = prices.find(p => p.item === `LOCKER${type}`);
    return obj ? Number(obj.price).toFixed(2) : "0.00";
  }

  getLockerDimensions(type) {
    const dims = {
      'BNS':   { ancho: 30, alto: 30,  fondo: 50 },
      'CNS':   { ancho: 30, alto: 30,  fondo: 50 },
      'BWS':   { ancho: 40, alto: 30,  fondo: 50 },
      'CWS':   { ancho: 40, alto: 30,  fondo: 50 },
      'BNM':   { ancho: 30, alto: 60,  fondo: 50 },
      'CNM':   { ancho: 30, alto: 60,  fondo: 50 },
      'BWM':   { ancho: 40, alto: 60,  fondo: 50 },
      'CWM':   { ancho: 40, alto: 60,  fondo: 50 },
      'BNL':   { ancho: 30, alto: 90,  fondo: 50 },
      'CNL':   { ancho: 30, alto: 90,  fondo: 50 },
      'BWL':   { ancho: 40, alto: 90,  fondo: 50 },
      'CWL':   { ancho: 40, alto: 90,  fondo: 50 },
      'BNXL':  { ancho: 30, alto: 120, fondo: 50 },
      'CNXL':  { ancho: 30, alto: 120, fondo: 50 },
      'BWXL':  { ancho: 40, alto: 120, fondo: 50 },
      'CWXL':  { ancho: 40, alto: 120, fondo: 50 },
      'BN2XL': { ancho: 30, alto: 150, fondo: 50 },
      'CN2XL': { ancho: 30, alto: 150, fondo: 50 },
      'BW2XL': { ancho: 40, alto: 150, fondo: 50 },
      'CW2XL': { ancho: 40, alto: 150, fondo: 50 },
      'BN3XL': { ancho: 30, alto: 190, fondo: 50 },
      'CN3XL': { ancho: 30, alto: 190, fondo: 50 },
      'BW3XL': { ancho: 40, alto: 190, fondo: 50 },
      'CW3XL': { ancho: 40, alto: 190, fondo: 50 },
    };
    return dims[type] || null;
  }

  getLockerUseCase(type) {
    const d = this.getLockerDimensions(type);
    if (!d) return null;
    const { ancho, alto } = d;
    const wide = ancho >= 40;

    if (alto <= 30) {
      return {
        label: wide ? "Artículos compactos y anchos" : "Artículos pequeños",
        desc: wide
          ? "Diseñada para cascos de bici o moto, bolsos de mano anchos y artículos personales voluminosos."
          : "Ideal para cascos de bicicleta, bolsos pequeños y artículos personales de bolsillo.",
        fits: wide
          ? ["🪖 Casco bici / moto", "👜 Bolso de mano", "📦 Artículos pequeños"]
          : ["🪖 Casco bicicleta", "👜 Bolso pequeño", "🎒 Mochila mini"],
      };
    }
    if (alto <= 60) {
      return {
        label: wide ? "Mochila urbana grande" : "Mochila urbana",
        desc: wide
          ? "Cabe una mochila de ciudad amplia, bolsa de la compra o bolso de hombro de gran tamaño."
          : "Perfecta para mochila de portátil, bolsa de tela o chaqueta doblada.",
        fits: wide
          ? ["🎒 Mochila urbana", "🛍️ Bolsa de compras", "👜 Bolso grande"]
          : ["🎒 Mochila portátil", "🧥 Chaqueta", "🛍️ Bolsa de tela"],
      };
    }
    if (alto <= 90) {
      return {
        label: wide ? "Maleta de cabina y mochilas grandes" : "Mochilas grandes y deporte",
        desc: wide
          ? "Admite mochilas de gran tamaño y maletas de cabina estándar (hasta 55 cm de alto, 40 cm de ancho)."
          : "Caben mochilas de trekking, bolsas de deporte y equipo de montaña.",
        fits: wide
          ? ["🧳 Maleta cabina", "🎒 Mochila grande", "⛷️ Botas de esquí"]
          : ["🎒 Mochila trekking", "🏋️ Bolsa de deporte", "🏕️ Equipo montaña"],
      };
    }
    if (alto <= 120) {
      return {
        label: wide ? "Maleta mediana" : "Equipaje alto y estrecho",
        desc: wide
          ? "Perfecta para maletas medianas de cabina y bolsas de deporte de gran tamaño."
          : "Indicada para maletas delgadas en posición vertical o bolsas de esquí estrechas.",
        fits: wide
          ? ["🧳 Maleta mediana", "🏋️ Bolsa deporte XL", "🎿 Bolsa de esquí"]
          : ["🧳 Maleta delgada", "🎿 Esquís", "🏄 Tabla estrecha"],
      };
    }
    if (alto <= 150) {
      return {
        label: wide ? "Maleta grande de facturación" : "Maleta alta y deporte",
        desc: wide
          ? "Para maletas grandes de facturación y equipos deportivos voluminosos."
          : "Para maletas altas y estrechas, bolsas de viaje largas o estuches de instrumentos.",
        fits: wide
          ? ["🧳 Maleta grande", "🎿 Equipo esquí", "🛹 Skateboard"]
          : ["🧳 Maleta alta", "🎸 Estuche instrumento", "🏋️ Equipo deportivo"],
      };
    }
    return {
      label: "Equipaje extra grande",
      desc: wide
        ? "Nuestra taquilla más grande. Admite maletas enormes, tablas de surf, bolsas de ski o bicicletas plegadas."
        : "Para equipos de gran formato: esquís, tablas largas, instrumentos o maletas extra altas.",
      fits: wide
        ? ["🧳 Maleta XXL", "🏄 Tabla de surf", "🚲 Bici plegable", "🎿 Bolsa de ski"]
        : ["🎿 Esquís", "🏄 Tabla larga", "🎻 Instrumento largo", "🧳 Maleta extra alta"],
    };
  }

  isBeforeToday(date) {
    const today = new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Madrid" });
    const d = new Date(date).toLocaleDateString("en-CA", { timeZone: "Europe/Madrid" });
    return d < today;
  }

  isTodayBlocked() {
    const { workingMode, hybridMode } = this.state;
    return workingMode !== "6" && hybridMode === true;
  }

  isToday(date) {
    const today = new Date().toLocaleDateString("en-CA", { timeZone: "Europe/Madrid" });
    const d = new Date(date).toLocaleDateString("en-CA", { timeZone: "Europe/Madrid" });
    return d === today;
  }

  isCurrentMonthOrBefore(date) {
    const today = new Date();
    return date.getFullYear() <= today.getFullYear() && date.getMonth() <= today.getMonth();
  }

  renderLockerGrid(onSelect) {
    const { lockers, prices, selectedLockerType } = this.state;
    const { t } = this.props;

    if (!lockers || !prices) {
      return <div className="spinner"><img src="/images/logo.png" alt="Logo" /></div>;
    }

    const cards = ORDERED_LOCKER_KEYS.filter(key => lockers[key]);

    if (cards.length === 0) {
      return (
        <div className="nm-sold-out" onClick={() => this.props.push('/lockerSelectorRentalView')}>
          <h5>{t("TAQUILLAS AGOTADAS")}</h5>
          <h6>{t("Haz click aquí para volver a selección de zonas")}</h6>
        </div>
      );
    }

    const fewCards = cards.length < 4;

    return (
      <div className={`nm-locker-grid${fewCards ? " nm-locker-grid-few" : ""}`}>
        {cards.map((key) => {
          const d = this.getLockerDimensions(key);
          return (
            <div
              key={key}
              className={`nm-locker-card${selectedLockerType === key ? " nm-locker-card-selected" : ""}`}
              onClick={() => onSelect(key)}
            >
              <button
                className="nm-locker-info-btn"
                onClick={(e) => { e.stopPropagation(); this.setState({ measureModal: true, measureType: key }); }}
                title={t("Ver información")}
              >
                <Info size={13} strokeWidth={2.5} />
              </button>
              <div className="nm-locker-img-wrap">
                <img src={this.getLockerPicture(key)} alt={key} className="nm-locker-img" />
              </div>
              <span className="nm-locker-type">{this.getLockerType(key)}</span>
              {d && (
                <span className="nm-locker-dims">{d.ancho} × {d.alto} × {d.fondo} cm</span>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  renderMeasureModal() {
    const { t } = this.props;
    const { measureModal, measureType } = this.state;
    if (!measureModal || !measureType) return null;

    const d = this.getLockerDimensions(measureType);
    const useCase = this.getLockerUseCase(measureType);
    const close = () => this.setState({ measureModal: false, measureType: null });

    return (
      <div className="nm-modal-overlay" onClick={close}>
        <div className="nm-modal nm-info-modal" onClick={(e) => e.stopPropagation()}>

          <div className="nm-info-hero">
            <button className="nm-info-back" onClick={close}>
              <ChevronLeft size={18} strokeWidth={2.5} />
              {t("Volver")}
            </button>
            <img
              src={this.getLockerPicture(measureType)}
              alt={measureType}
              className="nm-info-hero-img"
            />
            <h4 className="nm-info-hero-title">{this.getLockerType(measureType)}</h4>
          </div>

          {d && (
            <div className="nm-info-dims">
              <div className="nm-info-dim-block">
                <span className="nm-info-dim-value">{d.ancho}</span>
                <span className="nm-info-dim-unit">cm</span>
                <span className="nm-info-dim-label">{t("Ancho")}</span>
              </div>
              <div className="nm-info-dim-sep">×</div>
              <div className="nm-info-dim-block">
                <span className="nm-info-dim-value">{d.alto}</span>
                <span className="nm-info-dim-unit">cm</span>
                <span className="nm-info-dim-label">{t("Alto")}</span>
              </div>
              <div className="nm-info-dim-sep">×</div>
              <div className="nm-info-dim-block">
                <span className="nm-info-dim-value">{d.fondo}</span>
                <span className="nm-info-dim-unit">cm</span>
                <span className="nm-info-dim-label">{t("Fondo")}</span>
              </div>
            </div>
          )}

          {useCase && (
            <div className="nm-info-usecase">
              <div className="nm-info-usecase-header">
                <div className="nm-info-usecase-icon">📦</div>
                <span className="nm-info-usecase-heading">{t("¿Qué cabe en esta taquilla?")}</span>
              </div>
              <span className="nm-info-usecase-label">{t(useCase.label)}</span>
              <p className="nm-info-usecase-desc">{t(useCase.desc)}</p>
              <div className="nm-info-fits">
                {useCase.fits.map((item, i) => (
                  <span key={i} className="nm-info-fit-tag">{t(item)}</span>
                ))}
              </div>
            </div>
          )}

          <div className="nm-info-footer" />

        </div>
      </div>
    );
  }

  renderUnifiedForm() {
    const { t } = this.props;
    const { selectedLockerType, selectedQuantity, startDate, endDate, isDurationExceeded, totalAmount, appliedRule } = this.state;

    const qty = selectedQuantity == null ? 1 : Number(selectedQuantity);
    const canContinue = selectedLockerType && selectedQuantity && startDate && !isDurationExceeded;

    return (
      <div className="nm-form">
        <div className="nm-uf-row">
          <button type="button" className="nm-uf-btn" onClick={() => this.openModal("isTypeModalOpen")}>
            <span className="nm-uf-label">{t("Tipo de taquilla")}</span>
            <span className="nm-uf-output">
              {selectedLockerType ? this.getLockerType(selectedLockerType) : t("Selecciona...")}
            </span>
          </button>
        </div>

        <div className="nm-uf-row">
          <button
            type="button"
            className="nm-uf-btn"
            onClick={() => this.openModal("isQtyModalOpen")}
            disabled={!selectedLockerType}
          >
            <span className="nm-uf-label">{t("Cantidad")}</span>
            <span className="nm-uf-output">
              {qty} {qty === 1 ? t("taquilla") : t("taquillas")}
            </span>
          </button>
        </div>

        <div className="nm-uf-row">
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              className="nm-uf-btn"
              style={{ flex: 1 }}
              onClick={() => this.openModal("isDatesModalOpen")}
              disabled={!selectedLockerType}
            >
              <span className="nm-uf-label">{t("Inicio")}</span>
              <span className="nm-uf-output">{startDate ? startDate.toLocaleDateString() : t("DD/MM/YYYY")}</span>
            </button>
            <div style={{ width: 1, background: "rgba(0,0,0,0.07)", margin: "8px 0" }} />
            <button
              type="button"
              className="nm-uf-btn"
              style={{ flex: 1 }}
              onClick={() => this.openModal("isDatesModalOpen")}
              disabled={!selectedLockerType || !startDate}
            >
              <span className="nm-uf-label">{t("Fin")}</span>
              <span className="nm-uf-output">{endDate ? endDate.toLocaleDateString() : t("DD/MM/YYYY")}</span>
            </button>
          </div>
        </div>

        {this.state.errorMessage && (
          <p style={{ color: "#e63946", textAlign: "center", fontSize: "0.85rem" }}>
            {t("LO SENTIMOS PERO ESTAS FECHAS NO ESTÁN DISPONIBLES, PRUEBA OTRAS U OTRO TIPO DE TAQUILLAS.")}
          </p>
        )}

        {appliedRule && (
          <p style={{ textAlign: "center", fontSize: "0.85rem", color: "#059669" }}>
            {t("Descuento aplicado: {{discount}}% (desde los {{days}} días)", {
              discount: appliedRule.DiscountPercentage,
              days: appliedRule.MaxDays,
            })}
          </p>
        )}

        <button
          className={`nm-btn-continue${!canContinue || this.state.isLoading ? " disabled" : ""}`}
          disabled={!canContinue || this.state.isLoading}
          onClick={this.handleContinue}
        >
          {this.state.isLoading ? (
            <span className="nm-btn-spinner" />
          ) : (
            `${t("Continuar")} ${t("por")} ${totalAmount.toFixed(2)} ${currencySymbol()}`
          )}
        </button>

        {this.renderUnifiedModals()}
      </div>
    );
  }

  renderUnifiedModals() {
    const { t } = this.props;
    const { isTypeModalOpen, isQtyModalOpen, isDatesModalOpen, selectedQuantity, startDate, endDate, isDurationExceeded, appliedRule } = this.state;
    const qty = selectedQuantity == null ? 1 : Number(selectedQuantity);

    return (
      <>
        {isTypeModalOpen && (
          <div className="rm-modal-overlay" onClick={() => this.closeModal("isTypeModalOpen")}>
            <div className="rm-modal" onClick={e => e.stopPropagation()}>
              <div className="rm-modal-header">
                <h5 className="nm-text-dark text-center">{t("Selecciona el tipo de taquilla")}</h5>
              </div>
              <div className="rm-modal-body">
                {this.renderLockerGrid(key => { this.selectLockerType(key); this.closeModal("isTypeModalOpen"); this.openModal("isQtyModalOpen"); })}
              </div>
            </div>
          </div>
        )}

        {isQtyModalOpen && (
          <div className="rm-modal-overlay" onClick={() => this.closeModal("isQtyModalOpen")}>
            <div className="rm-modal" onClick={e => e.stopPropagation()}>
              <div className="rm-modal-header">
                <h5 className="nm-text-dark text-center">{t("Cantidad de taquillas")}</h5>
              </div>
              <div className="rm-modal-body" style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <QuantityRentalStepper
                  min={1}
                  max={10}
                  value={qty}
                  label={t("Selecciona cuántas taquillas necesitas")}
                  qrData={this.props.qrData}
                  onChange={v => this.setState({ selectedQuantity: v, startDate: null, endDate: null, selectedPrices: [], totalAmount: 0, isDurationExceeded: false, appliedRule: null })}
                />
                <p style={{ textAlign: "center", fontSize: "0.9rem", marginTop: 8 }}>
                  {selectedQuantity} {selectedQuantity > 1 ? t("taquillas seleccionadas") : t("taquilla seleccionada")}
                </p>
              </div>
              <div className="rm-modal-footer">
                <button className="nm-btn-continue" onClick={() => { this.closeModal("isQtyModalOpen"); this.openModal("isDatesModalOpen"); }}>{t("Confirmar")}</button>
              </div>
            </div>
          </div>
        )}

        {isDatesModalOpen && (
          <div className="rm-modal-overlay" onClick={() => this.closeModal("isDatesModalOpen")}>
            <div className="rm-modal" onClick={e => e.stopPropagation()}>
              <div className="rm-modal-header">
                <h5 className="nm-text-dark text-center">{t("Selecciona las fechas")}</h5>
              </div>
              <div className="rm-modal-body" style={{ textAlign: "center" }}>
                {this.renderPriceCalendar()}
                {isDurationExceeded && (
                  <p style={{ color: "#e63946", fontSize: "0.82rem", marginTop: 8 }}>
                    {t("El alquiler online está limitado a 14 días consecutivos...")}
                  </p>
                )}
              </div>
              <div className="rm-modal-footer">
                <div style={{ display: "flex", justifyContent: "space-around", marginBottom: 10 }}>
                  <div>
                    <div className="nm-uf-label">{t("Inicio")}</div>
                    <span>{startDate ? startDate.toLocaleDateString() : t("DD/MM/YYYY")}</span>
                  </div>
                  <div>
                    <div className="nm-uf-label">{t("Fin")}</div>
                    <span>{endDate ? endDate.toLocaleDateString() : t("DD/MM/YYYY")}</span>
                  </div>
                </div>
                {appliedRule && (
                  <p style={{ textAlign: "center", fontSize: "0.82rem", color: "#059669" }}>
                    {t("Descuento aplicado: {{discount}}% (desde los {{days}} días)", {
                      discount: appliedRule.DiscountPercentage,
                      days: appliedRule.MaxDays,
                    })}
                  </p>
                )}
                <button
                  className={`nm-btn-continue${!startDate || isDurationExceeded ? " disabled" : ""}`}
                  disabled={!startDate || isDurationExceeded}
                  onClick={() => this.closeModal("isDatesModalOpen")}
                >
                  {t("Confirmar")}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  renderUnifiedFormPc() {
    const { t } = this.props;
    const { selectedLockerType, selectedQuantity, startDate, endDate, isDurationExceeded, totalAmount, appliedRule } = this.state;

    const qty = selectedQuantity == null ? 1 : Number(selectedQuantity);
    const canContinue = selectedLockerType && selectedQuantity && startDate && !isDurationExceeded;

    return (
      <>
        <div className="nm-locker-sel-form" ref={this.formRef}>
          <button
            ref={this.typeBtnRef}
            type="button"
            className="nm-uf-btn"
            onClick={() => this.openPanel("type")}
          >
            <span className="nm-uf-label">{t("Tipo")}</span>
            <span className="nm-uf-output">
              {selectedLockerType ? this.getLockerType(selectedLockerType) : t("Selecciona...")}
            </span>
          </button>

          <button
            ref={this.qtyBtnRef}
            type="button"
            className="nm-uf-btn"
            onClick={() => this.openPanel("qty")}
            disabled={!selectedLockerType}
          >
            <span className="nm-uf-label">{t("Cantidad")}</span>
            <span className="nm-uf-output">
              {qty} {qty === 1 ? t("taquilla") : t("taquillas")}
            </span>
          </button>

          <button
            ref={this.datesStartBtnRef}
            type="button"
            className="nm-uf-btn"
            onClick={() => this.openPanel("dates")}
            disabled={!selectedLockerType}
          >
            <span className="nm-uf-label">{t("Inicio")}</span>
            <span className="nm-uf-output">{startDate ? startDate.toLocaleDateString() : t("DD/MM/YYYY")}</span>
          </button>

          <button
            type="button"
            className="nm-uf-btn"
            onClick={() => this.openPanel("dates")}
            disabled={!selectedLockerType || !startDate}
          >
            <span className="nm-uf-label">{t("Fin")}</span>
            <span className="nm-uf-output">{endDate ? endDate.toLocaleDateString() : t("DD/MM/YYYY")}</span>
          </button>

          {appliedRule && (
            <span style={{ fontSize: "0.8rem", color: "#059669", whiteSpace: "nowrap", alignSelf: "center" }}>
              -{appliedRule.DiscountPercentage}%
            </span>
          )}

          <button
            className={`nm-btn-continue${!canContinue || this.state.isLoading ? " disabled" : ""}`}
            disabled={!canContinue || this.state.isLoading}
            onClick={this.handleContinue}
          >
            {this.state.isLoading ? (
              <span className="nm-btn-spinner" />
            ) : (
              `${t("Continuar")} — ${totalAmount.toFixed(2)} ${currencySymbol()}`
            )}
          </button>
        </div>

        {this.state.errorMessage && (
          <p style={{ color: "#e63946", textAlign: "center", fontSize: "0.85rem" }}>
            {t("LO SENTIMOS PERO ESTAS FECHAS NO ESTÁN DISPONIBLES, PRUEBA OTRAS U OTRO TIPO DE TAQUILLAS.")}
          </p>
        )}

        {this.renderUnifiedPanels()}
      </>
    );
  }

  renderUnifiedPanels() {
    const { t } = this.props;
    const { activePanel, selectedQuantity, startDate, endDate, isDurationExceeded, appliedRule } = this.state;
    const qty = selectedQuantity == null ? 1 : Number(selectedQuantity);

    return (
      <>
        {activePanel === "type" && (
          <div className="nm-uf-panel show">
            <h5 className="nm-text-dark text-center mb-3">
              {t("Selecciona el tipo de taquilla")}
            </h5>
            {this.renderLockerGrid(key => { this.selectLockerType(key); this.openPanel("qty"); })}
          </div>
        )}

        {activePanel === "qty" && (
          <div className="nm-uf-panel text-center show">
            <h5 className="nm-text-dark text-center mb-3">{t("Cantidad de taquillas")}</h5>
            <QuantityRentalStepper
              min={1}
              max={10}
              value={qty}
              label={t("Selecciona cuántas taquillas necesitas")}
              qrData={this.props.qrData}
              onChange={v => this.setState({ selectedQuantity: v, startDate: null, endDate: null, selectedPrices: [], totalAmount: 0, isDurationExceeded: false, appliedRule: null })}
            />
            <p style={{ textAlign: "center", fontSize: "0.9rem", marginTop: 8 }}>
              {selectedQuantity} {selectedQuantity > 1 ? t("taquillas seleccionadas") : t("taquilla seleccionada")}
            </p>
          </div>
        )}

        {activePanel === "dates" && (
          <div className="nm-uf-panel text-center show">
            <h5 className="nm-text-dark text-center mb-3">{t("Selecciona las fechas")}</h5>
            <div style={{ height: 1, background: "rgba(0,0,0,0.07)", margin: "0 0 16px" }} />
            {isDurationExceeded && (
              <p style={{ color: "#e63946", fontSize: "0.82rem" }}>
                {t("El alquiler online está limitado a 14 días consecutivos...")}
              </p>
            )}
            {this.renderPriceCalendarPC()}
            {appliedRule && (
              <p style={{ textAlign: "center", fontSize: "0.82rem", color: "#059669", marginTop: 8 }}>
                {t("Descuento aplicado: {{discount}}% (desde los {{days}} días)", {
                  discount: appliedRule.DiscountPercentage,
                  days: appliedRule.MaxDays,
                })}
              </p>
            )}
          </div>
        )}
      </>
    );
  }

  render() {
    const { t } = this.props;
    const { lockers, prices } = this.state;
    const { zoneSelected, descriptionZone } = this.props.location.state;

    if (!lockers || !prices) {
      return (
        <div className="spinner">
          <img src="/images/logo.png" alt="Logo" />
        </div>
      );
    }

    const headerCard = (
      <div className="nm-page-header-card">
        <div className="nm-page-header-card-text">
          <h3>{t("RESERVA DE TAQUILLA")}</h3>
          <span className="nm-page-header-zone">{zoneSelected} — {descriptionZone}</span>
        </div>
        <Grid2x2Check className="nm-form-icon" strokeWidth={1} />
      </div>
    );

    return (
      <div className="rentalBackground">
        <AppRentalHeader />
        <NmStepBarRental step={2} />

        <div className="nm-page d-xl-none">
          {headerCard}
          {this.renderUnifiedForm()}
        </div>

        <div className="nm-page pc d-none d-xl-block">
          {headerCard}
          {this.renderUnifiedFormPc()}
        </div>

        {this.renderMeasureModal()}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  auth: true,
  lockers: state.lockers,
  prices: state.prices,
  qrData: state.qrs.qr,
});

const mapDispatchToProps = (dispatch) => ({
  push: bindActionCreators(push, dispatch),
  fetchDisponibility: bindActionCreators(fetchDisponibility, dispatch),
  fetchAvailability: bindActionCreators(fetchAvailability, dispatch),
  fetchCalendarByContract: bindActionCreators(fetchCalendarByContract, dispatch),
});

export default withNamespaces()(
  connect(mapStateToProps, mapDispatchToProps)(LockerSelectorRentalView)
);
